import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { competencyKeys, personKeys, roleKeys, roleLevelKeys, skillKeys } from "@/lib/query";

export interface RoleSkillRow {
  id: string;
  skillId: string;
  skillName: string;
}

export interface RoleCompetencyRow {
  id: string;
  competencyId: string;
  competencyName: string;
}

export interface RoleCertificationRow {
  id: string;
  certificationId: string;
  certificationName: string;
}

const skillsKey = (roleId: string) => [...roleKeys.detail(roleId), "skills"] as const;
const competenciesKey = (roleId: string) => [...roleKeys.detail(roleId), "competencies"] as const;
const certificationsKey = (roleId: string) =>
  [...roleKeys.detail(roleId), "certifications"] as const;

export function useRoleSkills(roleId: string) {
  return useQuery({
    queryKey: skillsKey(roleId),
    queryFn: async (): Promise<RoleSkillRow[]> => {
      const res = await fetch(`/api/roles/${roleId}/skills`);
      if (!res.ok) throw new Error("Failed to fetch role skills");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useRoleCompetencies(roleId: string) {
  return useQuery({
    queryKey: competenciesKey(roleId),
    queryFn: async (): Promise<RoleCompetencyRow[]> => {
      const res = await fetch(`/api/roles/${roleId}/competencies`);
      if (!res.ok) throw new Error("Failed to fetch role competencies");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useRoleCertifications(roleId: string) {
  return useQuery({
    queryKey: certificationsKey(roleId),
    queryFn: async (): Promise<RoleCertificationRow[]> => {
      const res = await fetch(`/api/roles/${roleId}/certifications`);
      if (!res.ok) throw new Error("Failed to fetch role certifications");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useSetRoleSkills(roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { skillId: string }[]) => {
      const res = await fetch(`/api/roles/${roleId}/skills`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
      // A role's required skills feed the skill People tabs and the person
      // directory's derived skill chips/filter (person → role → skill).
      qc.invalidateQueries({ queryKey: skillKeys.all });
      qc.invalidateQueries({ queryKey: personKeys.all });
      // The level expectation grid lists a row per required skill/competency
      // and lives under its own key root, so the roleKeys.detail invalidation
      // above doesn't reach it. Without this the grid keeps its cached rows —
      // on a role whose first requirement was just added, that's the "this role
      // doesn't require any skills or competencies yet" empty state persisting
      // after a save that plainly worked.
      qc.invalidateQueries({ queryKey: roleLevelKeys.expectationsForRole(roleId) });
      toast.success("Required skills updated");
    },
    onError: (e: Error) => toast.error(e.message ?? "Failed to update required skills"),
  });
}

export function useSetRoleCompetencies(roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { competencyId: string }[]) => {
      const res = await fetch(`/api/roles/${roleId}/competencies`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
      // A role's required competencies feed the competency People tabs
      // (person → role → competency).
      qc.invalidateQueries({ queryKey: competencyKeys.all });
      // Same as required skills — the grid's rows come from both sets.
      qc.invalidateQueries({ queryKey: roleLevelKeys.expectationsForRole(roleId) });
      toast.success("Required competencies updated");
    },
    onError: (e: Error) => toast.error(e.message ?? "Failed to update required competencies"),
  });
}

export function useSetRoleCertifications(roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: { certificationId: string }[]) => {
      const res = await fetch(`/api/roles/${roleId}/certifications`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Update failed");
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roleKeys.detail(roleId) });
      toast.success("Required certifications updated");
    },
    onError: (e: Error) => toast.error(e.message ?? "Failed to update required certifications"),
  });
}
